import {GritProjectService} from "../../services/grit/grit.project.service";
import {VectorLayerDMTR} from "../../menu/view-menu/layer";
import * as olProj from "ol/proj";
import {Projection} from "ol/proj";
import {GritFeature} from "./grit.feature";
import {FeatureType} from "../EnumTypes";
import {GritCoordinates, GritCoordinateType, RCS} from "./grit.coordinates";
import {GritCondition, GritConditionType,} from "./grit.filter";
import Circle from "ol/geom/Circle";
import {GritInputCondition, GritModel, GritOption, GritTableField} from "./grit.model";
import {GritDynamicTableType} from "./grit.enums";
import Polygon, {circular, fromCircle} from "ol/geom/Polygon";
import { VicentyToolControlService } from "src/app/services/vicenty-tool-control.service";
import { Coordinate } from "ol/coordinate";


export class RadarModel implements GritModel<RadarModel> {
  id: number = 0;

  groupVisibility: string = "";
  groupAccess: string = "";
  groupOriginator: string = "";

  name: string = "";
  latitude: GritCoordinates = new GritCoordinates(GritCoordinateType.Latitude);
  longitude: GritCoordinates = new GritCoordinates(GritCoordinateType.Longitude);
  radius: number = 0;

  firs: string[];

  public toDTO() {
    return {
      id: this.id,

      groupVisibility: this.groupVisibility,
      groupAccess: this.groupAccess,
      groupOriginator: this.groupOriginator,

      name: this.name,
      latitude: this.latitude.value,
      longitude: this.longitude.value,
      radius: this.radius,

      firs: JSON.stringify(this.firs),
    }
  }

  public fromDTO(radarDTO: any) {
    this.id = radarDTO.id;

    this.groupVisibility = radarDTO.groupVisibility;
    this.groupAccess = radarDTO.groupAccess;
    this.groupOriginator = radarDTO.groupOriginator;

    this.name = radarDTO.name;
    this.latitude = new GritCoordinates(GritCoordinateType.Latitude, radarDTO.latitude);
    this.longitude = new GritCoordinates(GritCoordinateType.Longitude, radarDTO.longitude);
    this.radius = radarDTO.radius;

    this.firs = JSON.parse(radarDTO.firs);

    return this;
  }

  public createFeature(layer: VectorLayerDMTR, projection: Projection): GritFeature<RadarModel> {
    return new GritFeature(this.id, {
      featureType: FeatureType.Radar,
      layer: layer,
      geometry: VicentyToolControlService.createEllipseGeometry(
        [this.longitude.value, this.latitude.value],
        this.radius * 1000,
        projection
      ),
      entity: this
    })
  }


  public static getCircleRadiusForProjection(radius, projection: Projection): number {
    switch (projection.getUnits()) {
      case 'm':
        // If the units are meters, use the radius directly in meters
        return radius;
      case 'degrees':
        // Convert meters to degrees (approximate conversion for latitude)
        const metersToDegrees = 1 / 111320; // 1 degree latitude ≈ 111.32 km
        return radius * metersToDegrees;
      case 'ft':
        // Convert meters to feet
        const metersToFeet = 3.28084;
        return radius * metersToFeet;
      case 'us-ft':
        // Convert meters to US survey feet
        const metersToUSSurveyFeet = 3.28083333333;
        return radius * metersToUSSurveyFeet;
      case 'radians':
        // Convert meters to radians; you may need a specific conversion factor depending on context
        const metersToRadians = 1 / (6371000 * 2 * Math.PI / 360); // Approximation based on Earth's radius
        return radius * metersToRadians;
      default:
        console.warn("Unsupported units on Circle Radius:", projection.getUnits());
        return radius; // Default to meters if units are unknown
    }
  }

  identifyToolOutput(){
    return `
        <h4 style="font-weight:bold">Radar</h4>
        <p>Name: ${this.name}</p>
        <p>Latitude: ${this.latitude.value}</p>
        <p>Longitude: ${this.longitude.value}</p>
        <p>Radius: ${this.radius}</p>
        <p>Firs: ${this.firs.map(value => GritProjectService.globalInformation.firs.find(o => o.value === value.toString()).display ?? "")}</p>
        <p>Visibility: ${this.groupVisibility}</p>
        <p>Access: ${this.groupAccess}</p>
        <p>Originator: ${this.groupOriginator}</p>
        `
  }

  static fieldsValues: Record<string, GritOption<string>[]> = {}

  static configuration: Record<string, GritOption<any>[]> = {
    table: [
      {
        display: "Name",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: RadarModel) {
            return entity.name;
          }
        }
      },
      {
        display: "Latitude",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: RadarModel) {
            return String(entity.latitude.value);
          }
        }
      },
      {
        display: "Longitude",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: RadarModel) {
            return String(entity.longitude.value);
          }
        }
      },
      {
        display: "Radius",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: RadarModel) {
            return String(entity.radius);
          }
        }
      },
      {
        display: "FIRS",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: RadarModel) {
            return entity.firs
              .map(fir => GritProjectService.globalInformation.firs
                .find(o => o.value == fir).display).toString()
          }
        }
      },
    ] as GritOption<GritTableField<RadarModel>>[],

    filters: [
      {
        display: "Country",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => GritProjectService.globalInformation.countries ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("firs", GritConditionType.Includes_StartsWith, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return GritProjectService.globalInformation.countries.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "FIR",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => GritProjectService.globalInformation.firs ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("firs", GritConditionType.Includes_Equal, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return GritProjectService.globalInformation.firs.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
    //   {
    //   display: "Shared",
    //   value: {
    //     inputs: [
    //       {
    //         field: "Value",
    //         type: "dropdown",
    //         value: () => GritProjectService.globalInformation.boolean ?? []
    //       },
    //     ],
    //     condition: function (values: string[]): GritCondition[] {
    //       let condition = new GritCondition("shared", GritConditionType.Equal, values[0]);
    //       return [condition]
    //     },
    //     toString(conditions: GritCondition[]): string {
    //       return GritProjectService.globalInformation.boolean.find(option => option.value == conditions[0].value).display ?? "";
    //     }
    //   }
    // },
      {
        display: "Range",
        value: {
          inputs: [
            {
              field: "Greater Than",
              type: "number",
              value: () => 0,
            },
            {
              field: "Lower Than",
              type: "number",
              value: () => 500,
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition1 = new GritCondition("radius", GritConditionType.GreaterThanEqual, values[0]);
            let condition2 = new GritCondition("radius", GritConditionType.LowerThanEqual, values[1]);
            return [condition1, condition2]
          },

          toString: function (conditions: GritCondition[]) : string{
            return `${conditions[0].value} < range < ${conditions[1].value}`
          }
        }
      },
    ] as GritOption<GritInputCondition>[],

    search: [
      {
        display: "All",
        value: {
          inputs: [],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("id", GritConditionType.StartsWith, "");
            return [condition]
          }
        }
      },
      {
        display: "Country",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => GritProjectService.globalInformation.countries ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("firs", GritConditionType.Includes_StartsWith, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return GritProjectService.globalInformation.countries.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "FIR",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => GritProjectService.globalInformation.firs ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("firs", GritConditionType.Includes_Equal, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return GritProjectService.globalInformation.firs.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "Name",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("name", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Latitude",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("latitude.value", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Longitude",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("longitude.value", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Range",
        value: {
          inputs: [
            {
              field: "Value",
              type: "number",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("radius", GritConditionType.Equal, values[0]);
            return [condition]
          }
        }
      },
      // {
      //   display: "Shared",
      //   value: {
      //     inputs: [
      //       {
      //         field: "Value",
      //         type: "dropdown",
      //         value: () => GritProjectService.globalInformation.boolean ?? []
      //       },
      //     ],
      //     condition: function (values: string[]): GritCondition[] {
      //       let condition = new GritCondition("shared", GritConditionType.Equal, values[0]);
      //       return [condition]
      //     },
      //     toString(conditions: GritCondition[]): string {
      //       return GritProjectService.globalInformation.boolean.find(option => option.value == conditions[0].value).display ?? "";
      //     }
      //   }
      // },
    ] as GritOption<GritInputCondition>[]
  }
}
